import type { DraftOption } from '../components/OptionPicker';
import type { FriendDraft } from './apiV2';

/**
 * Create-flow draft persistence. Autosaved to localStorage on every change so
 * a first-run host can leave for the magic-link sign-in and come back
 * (?draft=1) to screen 3 with everything intact.
 */

const DRAFT_KEY = 'bfn_draft_v1';

export interface CreateDraft {
  v: 1;
  step: 1 | 2 | 3;
  title: string;
  titleEdited: boolean;
  /** datetime-local string (YYYY-MM-DDTHH:mm, host-local) */
  startsLocal: string;
  roomCode: string;
  note: string;
  freq: 'none' | 'weekly' | 'biweekly' | 'monthly';
  weekday: number;
  options: DraftOption[];
  /** ids of saved friends checked on screen 2 */
  selectedFriendIds: string[];
  /** favorites were pre-checked once — don't re-check after deliberate unchecks */
  selectionInitialized: boolean;
  /** friends added inline while signed out — saved to /api/friends at send time */
  pendingFriends: FriendDraft[];
  savedAt: number;
}

/** Default the coming Thursday 8pm — the crew's actual cadence. Planning ON a
 * Thursday before showtime means TONIGHT (the product is named Thursday
 * Night), not next week. */
export function defaultStart(): string {
  const now = new Date();
  const d = new Date(now);
  d.setHours(20, 0, 0, 0);
  const isThursdayBeforeShowtime = now.getDay() === 4 && now.getTime() < d.getTime();
  if (!isThursdayBeforeShowtime) {
    const days = (4 - now.getDay() + 7) % 7 || 7; // next Thursday (4)
    d.setDate(d.getDate() + days);
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

export function emptyDraft(): CreateDraft {
  return {
    v: 1,
    step: 1,
    title: 'Thursday Night',
    titleEdited: false,
    startsLocal: defaultStart(),
    roomCode: '',
    note: '',
    freq: 'weekly',
    weekday: 4,
    options: [{ title: '' }],
    selectedFriendIds: [],
    selectionInitialized: false,
    pendingFriends: [],
    savedAt: 0,
  };
}

export function loadDraft(): CreateDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as CreateDraft;
    if (d?.v !== 1) return null;
    // drafts older than 7 days are stale — a new night starts fresh
    if (!d.savedAt || Date.now() - d.savedAt > 7 * 24 * 3600 * 1000) return null;
    return { ...emptyDraft(), ...d };
  } catch {
    return null;
  }
}

export function saveDraft(d: CreateDraft): void {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...d, savedAt: Date.now() }));
  } catch {
    // storage full/blocked — the flow still works, just without persistence
  }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    // ignore
  }
}
